import { inject, Injectable } from '@angular/core';
import { Upgrade } from '@core/models';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { AnalyticsService } from '@core/services/analytics';
import { PrivateSellingService } from '@core/services/private-selling.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UpgradesService {
  private privateSellingService = inject(PrivateSellingService);
  private analyticsService = inject(AnalyticsService);
  private adobeDataLayerService = inject(AdobeDataLayerService);

  upgrades: Upgrade[] | undefined;
  totalCartAndFiltersSeats$ = new Subject<number>();
  upgradesCall: ReturnType<typeof setTimeout>;

  reachingUpgrade: Upgrade | null = null;
  acquiredUpgrade: Upgrade | null = null;

  availableUpgrades: Upgrade[] = [];
  acquiredUpgrades: Upgrade[] = [];
  previousTotalSeats: number;
  private previousCartSeats: number | undefined;
  private syntheticCartEmissionDepth = 0;

  beginSyntheticCartEmission(): void {
    this.syntheticCartEmissionDepth += 1;
  }

  endSyntheticCartEmission(): void {
    this.syntheticCartEmissionDepth = Math.max(
      0,
      this.syntheticCartEmissionDepth - 1
    );
  }

  get isSyntheticCartEmission(): boolean {
    return this.syntheticCartEmissionDepth > 0;
  }

  sendLastUpgradesCall(numSeats: number) {
    if (this.upgradesCall) {
      clearTimeout(this.upgradesCall);
    }
    this.upgradesCall = setTimeout(() => {
      this.totalCartAndFiltersSeats$.next(numSeats);
    }, 1000);
  }

  sortUpgradesByGoal(upgrades: Upgrade[]) {
    if (upgrades?.length) {
      upgrades.sort((a, b) => a.goal - b.goal);
    }
  }

  checkCartSeatsForUpgradesList(totalCartSeats: number) {
    if (!this.upgrades?.length) return;

    this.sortUpgradesByGoal(this.upgrades);
    this.availableUpgrades = [];
    this.acquiredUpgrades = [];

    for (const upgrade of this.upgrades) {
      if (upgrade.goal <= totalCartSeats) {
        this.acquiredUpgrades.push(upgrade);
      } else {
        this.availableUpgrades.push(upgrade);
      }
    }
  }

  checkTotalSeatsForUpgradesMessages(
    totalSeats: number,
    withAnalytics: boolean = true
  ) {
    if (!this.upgrades?.length) return;
    if (
      this.privateSellingService.isEnabled() &&
      !this.privateSellingService.isUserInWhitelist()
    ) {
      return;
    }

    this.sortUpgradesByGoal(this.upgrades);

    let availableUps = [];
    let acquiredUps = [];
    for (const upgrade of this.upgrades) {
      if (upgrade.goal <= totalSeats) {
        acquiredUps.push(upgrade);
      } else {
        availableUps.push(upgrade);
      }
    }

    const gameId = this.adobeDataLayerService.getEventId() || undefined;
    const analyticsEnabled = withAnalytics && !this.isSyntheticCartEmission;
    const crossingBaseline = this.previousCartSeats ?? 0;
    const crossedUpwardTo = (threshold: number): boolean =>
      analyticsEnabled &&
      totalSeats > crossingBaseline &&
      threshold > crossingBaseline &&
      threshold <= totalSeats;

    for (const upgrade of this.upgrades) {
      if (upgrade.goal === totalSeats) {
        this.acquiredUpgrade = upgrade;
        setTimeout(() => (this.acquiredUpgrade = null), 3000);
        if (crossedUpwardTo(upgrade.goal)) {
          this.analyticsService.trackUpgradeAcquired({
            upgradeId: String(upgrade.id),
            upgradeName: upgrade.name,
            goal: upgrade.goal,
            seatsInCart: totalSeats,
            gameId,
          });
        }
      } else if (upgrade.minSeatsToShow === totalSeats) {
        this.reachingUpgrade = upgrade;
        setTimeout(() => (this.reachingUpgrade = null), 3000);
        if (crossedUpwardTo(upgrade.minSeatsToShow)) {
          this.analyticsService.trackUpgradeShown({
            upgradeId: String(upgrade.id),
            upgradeName: upgrade.name,
            goal: upgrade.goal,
            minSeatsToShow: upgrade.minSeatsToShow,
            seatsInCart: totalSeats,
            gameId,
          });
        }
      }
    }

    if (
      this.previousTotalSeats - totalSeats < -1 ||
      this.previousTotalSeats - totalSeats > 1
    ) {
      if (totalSeats > this.previousTotalSeats) {
        for (const upgrade of this.upgrades) {
          const wasCrossed =
            upgrade.goal > this.previousTotalSeats &&
            upgrade.goal <= totalSeats;
          if (!wasCrossed) {
            continue;
          }
          this.acquiredUpgrade = upgrade;
          setTimeout(() => (this.acquiredUpgrade = null), 3000);
          if (crossedUpwardTo(upgrade.goal)) {
            this.analyticsService.trackUpgradeAcquired({
              upgradeId: String(upgrade.id),
              upgradeName: upgrade.name,
              goal: upgrade.goal,
              seatsInCart: totalSeats,
              gameId,
            });
          }
        }
      }

      if (availableUps[0] && availableUps[0].minSeatsToShow < totalSeats) {
        this.reachingUpgrade = availableUps[0];
        setTimeout(() => (this.reachingUpgrade = null), 3000);
        if (crossedUpwardTo(availableUps[0].minSeatsToShow)) {
          this.analyticsService.trackUpgradeShown({
            upgradeId: String(availableUps[0].id),
            upgradeName: availableUps[0].name,
            goal: availableUps[0].goal,
            minSeatsToShow: availableUps[0].minSeatsToShow,
            seatsInCart: totalSeats,
            gameId,
          });
        }
      }
    }

    this.previousTotalSeats = totalSeats;
    if (withAnalytics) {
      this.previousCartSeats = totalSeats;
    }
  }
}
